import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { WorkflowRequest, StageHistoryItem, DocumentType, WorkflowDocument } from '../../types';
import { ArrowRight, Upload, Trash2, Edit, Save, Check, X, FileText, Download, CheckCircle, Truck, Ship, CalendarDays, Timer, Package, User, Clock, AlertCircle, MoreVertical, Paperclip } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import useAppStore from '../../store/useAppStore';
import toast from 'react-hot-toast';
import { differenceInDays, format, differenceInMilliseconds } from 'date-fns';
import { Badge } from '../../components/ui/Badge';
import { WORKFLOW_STAGES } from '../../data/mockData';

// Helper function for precise time ago formatting
const timeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 30) return `منذ بضع ثوان`;

    let interval = seconds / 31536000;
    if (interval > 1) return `منذ ${Math.floor(interval)} سنوات`;
    interval = seconds / 2592000;
    if (interval > 1) return `منذ ${Math.floor(interval)} أشهر`;
    interval = seconds / 86400;
    if (interval > 1) return `منذ ${Math.floor(interval)} أيام`;
    interval = seconds / 3600;
    if (interval > 1) return `منذ ${Math.floor(interval)} ساعات`;
    interval = seconds / 60;
    if (interval > 1) return `منذ ${Math.floor(interval)} دقائق`;
    return `منذ ${Math.floor(seconds)} ثوان`;
};

const ModernStageEditModal: React.FC<{
    item: StageHistoryItem;
    onClose: () => void;
    onSave: (updatedItem: StageHistoryItem, newFiles: { file: File; type: string; id: string }[]) => Promise<void>;
}> = ({ item, onClose, onSave }) => {
    const [comment, setComment] = useState(item.comment);
    const [documents, setDocuments] = useState<WorkflowDocument[]>(item.documents);
    const [newFiles, setNewFiles] = useState<{ file: File; type: string; id: string }[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedDocType, setSelectedDocType] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const stageConfig = WORKFLOW_STAGES.find(s => s.id === item.stageId);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const addedFiles = Array.from(e.target.files).map((file: File) => {
                const fileId = `DOC-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
                setDocuments(prev => [...prev, { id: fileId, url: URL.createObjectURL(file), fileName: file.name, type: selectedDocType as DocumentType, uploadDate: new Date().toISOString(), file }]);
                return { file, type: selectedDocType, id: fileId };
            });
            setNewFiles(prev => [...prev, ...addedFiles]);
        }
        e.target.value = '';
    };

    const handleUploadClick = (type: string) => {
        setSelectedDocType(type);
        fileInputRef.current?.click();
    };

    const removeDocument = (docId: string) => {
        setDocuments(prev => prev.filter(d => d.id !== docId));
        setNewFiles(prev => prev.filter(f => f.id !== docId));
    };

    const handleSave = async () => {
        setIsSaving(true);
        const updatedItem = { ...item, comment, documents };
        await onSave(updatedItem, newFiles);
        setIsSaving(false);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="glass-panel rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-700/50 overflow-hidden" onClick={e => e.stopPropagation()}>
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
                <div className="p-6 border-b border-slate-700/50 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-white">تعديل المرحلة: {item.stageName}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                        <X size={24} />
                    </button>
                </div>
                <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                    <div>
                        <label className="text-sm font-medium mb-2 block text-slate-300">التعليق</label>
                        <textarea
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-4 text-white focus:outline-none focus:border-blue-500/50 min-h-[120px]"
                            placeholder="أضف تعليقاً..."
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium mb-2 block text-slate-300">المستندات</label>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {stageConfig?.requiredDocuments?.map(docType => (
                                <button
                                    key={docType}
                                    onClick={() => handleUploadClick(docType)}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors text-sm border border-slate-700"
                                >
                                    <Upload size={14} />
                                    <span>إضافة/تغيير {docType}</span>
                                </button>
                            ))}
                        </div>
                        <div className="space-y-2 p-4 bg-slate-900/30 rounded-xl border border-slate-700/30">
                            {documents.map(doc => (
                                <div key={doc.id} className="flex items-center justify-between bg-slate-800/50 p-3 rounded-lg border border-slate-700/50 group">
                                    <div className="flex items-center gap-3 truncate">
                                        <div className="p-2 rounded-lg bg-blue-500/10 text-blue-400">
                                            <FileText size={16} />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-sm text-white truncate font-medium">{doc.fileName}</span>
                                            <span className="text-xs text-slate-400">{doc.type}</span>
                                        </div>
                                    </div>
                                    <button onClick={() => removeDocument(doc.id)} className="text-slate-500 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/10 transition-all">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                            {documents.length === 0 && <p className="text-sm text-center text-slate-500 py-4">لا توجد مستندات مرفقة.</p>}
                        </div>
                    </div>
                </div>
                <div className="p-6 border-t border-slate-700/50 flex justify-end gap-3 bg-slate-900/30">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors"
                    >
                        إلغاء
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-6 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-500 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : <Save size={18} />}
                        <span>حفظ التعديلات</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

const NumberInput: React.FC<{ label: string; value: number; onChange: (value: number) => void; icon: React.ReactNode; }> = ({ label, value, onChange, icon }) => (
    <div>
        <label className="block text-sm font-medium mb-2 text-slate-300">{label}</label>
        <div className="flex items-center bg-slate-900/50 border border-slate-700 rounded-xl overflow-hidden">
            <button type="button" onClick={() => onChange(Math.max(0, value - 1))} className="px-4 py-2.5 hover:bg-slate-800 text-slate-400 transition-colors border-l border-slate-700">-</button>
            <div className="relative flex-1">
                <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3 text-slate-500">
                    {icon}
                </div>
                <input
                    type="number"
                    value={value}
                    onChange={e => onChange(Number(e.target.value))}
                    className="w-full bg-transparent text-center py-2.5 text-white focus:outline-none"
                    min="0"
                />
            </div>
            <button type="button" onClick={() => onChange(value + 1)} className="px-4 py-2.5 hover:bg-slate-800 text-slate-400 transition-colors border-r border-slate-700">+</button>
        </div>
    </div>
);

const ApprovalStageForm: React.FC<{
    container20ft: number;
    setContainer20ft: (val: number) => void;
    container40ft: number;
    setContainer40ft: (val: number) => void;
    departureDate: string;
    setDepartureDate: (val: string) => void;
    departurePort: string;
    setDeparturePort: (val: string) => void;
}> = ({
    container20ft, setContainer20ft,
    container40ft, setContainer40ft,
    departureDate, setDepartureDate,
    departurePort, setDeparturePort
}) => (
        <div className="space-y-6 pt-6 border-t border-slate-700/50 mt-6">
            <h4 className="font-bold text-white flex items-center gap-2">
                <Package size={20} className="text-blue-400" />
                معلومات الشحنة
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <NumberInput label="عدد الحاويات (20 قدم)" value={container20ft} onChange={setContainer20ft} icon={<Truck size={16} />} />
                <NumberInput label="عدد الحاويات (40 قدم)" value={container40ft} onChange={setContainer40ft} icon={<Truck size={16} />} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium mb-2 text-slate-300">تاريخ المغادرة</label>
                    <div className="relative">
                        <CalendarDays size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            type="date"
                            value={departureDate}
                            onChange={e => setDepartureDate(e.target.value)}
                            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pr-10 pl-4 text-white focus:outline-none focus:border-blue-500/50"
                            required
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-2 text-slate-300">ميناء المغادرة</label>
                    <div className="relative">
                        <Ship size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            type="text"
                            value={departurePort}
                            onChange={e => setDeparturePort(e.target.value)}
                            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pr-10 pl-4 text-white focus:outline-none focus:border-blue-500/50"
                            required
                        />
                    </div>
                </div>
            </div>
        </div>
    );

const DepartureCountdown: React.FC<{ request: WorkflowRequest }> = ({ request }) => {
    const approvalItem = request.stageHistory.find(item => item.stageId === 2);

    if (!request.expectedDepartureDate || !approvalItem || isNaN(new Date(request.expectedDepartureDate).getTime())) {
        return null;
    }

    const now = new Date();
    const startDate = new Date(approvalItem.timestamp);
    const endDate = new Date(request.expectedDepartureDate);

    const totalDuration = differenceInMilliseconds(endDate, startDate);
    const elapsedDuration = differenceInMilliseconds(now, startDate);

    const progress = totalDuration > 0 ? Math.max(0, Math.min((elapsedDuration / totalDuration), 1)) : 0;

    const daysLeft = differenceInDays(endDate, now);

    let color = 'text-emerald-500';
    let pulseClass = '';
    if (daysLeft <= 3) {
        color = 'text-red-500';
        pulseClass = 'animate-pulse-red';
    } else if (daysLeft <= 7) {
        color = 'text-amber-500';
    }

    const isPast = now > endDate;

    const radius = 60;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - progress * circumference;

    return (
        <div className="glass-panel p-6 rounded-2xl flex flex-col items-center gap-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500"></div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Timer size={20} className="text-blue-400" />
                المدة المتبقية للمغادرة
            </h3>
            <div className={`relative w-40 h-40 ${pulseClass} rounded-full`}>
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 140 140">
                    <circle
                        className="text-slate-800"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="transparent"
                        r={radius}
                        cx="70"
                        cy="70"
                    />
                    <circle
                        className={`${color} transition-all duration-1000 ease-out`}
                        stroke="currentColor"
                        strokeWidth="8"
                        strokeLinecap="round"
                        fill="transparent"
                        r={radius}
                        cx="70"
                        cy="70"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-4xl font-bold ${color}`}>{isPast ? 0 : daysLeft}</span>
                    <span className="text-sm text-slate-400">{isPast ? 'يوم' : 'يوم متبقٍ'}</span>
                </div>
            </div>
            <div className="text-center bg-slate-900/50 px-6 py-3 rounded-xl border border-slate-700/50 w-full">
                <p className="text-sm text-slate-400 mb-1">
                    {isPast ? 'غادرت الشحنة في' : 'تاريخ المغادرة'}
                </p>
                <p className="font-bold text-white text-lg font-mono">{format(endDate, 'dd/MM/yyyy')}</p>
            </div>
        </div>
    );
};


const ModernWorkflowDetailScreen: React.FC<{ request: WorkflowRequest }> = ({ request }) => {
    const { user } = useAppContext();
    const { updateRequest } = useAppStore();
    const navigate = useNavigate();
    const [comment, setComment] = useState('');
    const [stagedFiles, setStagedFiles] = useState<{ file: File, type: DocumentType }[]>([]);
    const [selectedDocTypeForUpload, setSelectedDocTypeForUpload] = useState<DocumentType | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [editingHistoryItem, setEditingHistoryItem] = useState<StageHistoryItem | null>(null);

    const [container20ft, setContainer20ft] = useState(request.containerCount20ft || 0);
    const [container40ft, setContainer40ft] = useState(request.containerCount40ft || 0);
    const [departureDate, setDepartureDate] = useState(request.expectedDepartureDate || '');
    const [departurePort, setDeparturePort] = useState(request.departurePort || '');
    const [blNumber, setBlNumber] = useState(request.blNumber || '');
    const [blDate, setBlDate] = useState(request.blDate || '');
    const [ciNumber, setCiNumber] = useState(request.ciNumber || '');
    const [goodsType, setGoodsType] = useState(request.goodsType || '');
    // Basic dates editable from details view
    const [manufacturingDate, setManufacturingDate] = useState(request.manufacturingDate || '');
    const [expectedArrivalDate, setExpectedArrivalDate] = useState(request.expectedArrivalDate || '');

    const basicInfoSaveTimeoutRef = useRef<number | null>(null);
    useEffect(() => {
        if (!user) return;
        if (basicInfoSaveTimeoutRef.current) clearTimeout(basicInfoSaveTimeoutRef.current as any);
        basicInfoSaveTimeoutRef.current = window.setTimeout(async () => {
            const now = new Date().toISOString();
            const updated: WorkflowRequest = {
                ...request,
                containerCount20ft: Number(container20ft) || 0,
                containerCount40ft: Number(container40ft) || 0,
                departurePort: departurePort || undefined,
                blNumber: blNumber || undefined,
                blDate: blDate || undefined,
                ciNumber: ciNumber || undefined,
                goodsType: goodsType || undefined,
                lastModified: now,
            };
            await updateRequest(updated);
        }, 500);
        return () => {
            if (basicInfoSaveTimeoutRef.current) clearTimeout(basicInfoSaveTimeoutRef.current as any);
        };
    }, [container20ft, container40ft, departurePort, blNumber, blDate, ciNumber, goodsType]);

    const ARRIVAL_MARKER = '[ARRIVAL_DATE]';
    const upsertArrivalDateInHistory = (req: WorkflowRequest, date: string, processor: string): WorkflowRequest => {
        const currentStage = WORKFLOW_STAGES.find(s => s.id === req.currentStageId);
        const nowIso = new Date().toISOString();
        const newHistory = [...(req.stageHistory || [])];
        let updated = false;
        for (let i = newHistory.length - 1; i >= 0; i--) {
            const item = newHistory[i];
            if (item.comment && item.comment.startsWith(ARRIVAL_MARKER)) {
                newHistory[i] = { ...item, comment: `${ARRIVAL_MARKER}${date}`, modified: { processor, timestamp: nowIso } } as any;
                updated = true;
                break;
            }
        }
        if (!updated) {
            newHistory.push({
                stageId: req.currentStageId,
                stageName: currentStage?.name || `مرحلة ${req.currentStageId}`,
                processor,
                timestamp: nowIso,
                comment: `${ARRIVAL_MARKER}${date}`,
                documents: [],
            } as any);
        }
        return { ...req, stageHistory: newHistory, lastModified: nowIso } as WorkflowRequest;
    };

    const currentStage = WORKFLOW_STAGES.find(s => s.id === request.currentStageId)!;

    const areAllRequiredDocsStaged = useMemo(() => {
        if (!currentStage.requiredDocuments || currentStage.requiredDocuments.length === 0) return true;
        const stagedTypes = new Set(stagedFiles.map(f => f.type));
        return currentStage.requiredDocuments.every(reqDoc => stagedTypes.has(reqDoc));
    }, [stagedFiles, currentStage]);

    const handleApprove = async () => {
        if (request.currentStageId >= WORKFLOW_STAGES.length || !user) return;

        if (request.currentStageId === 2 && (!departureDate || !departurePort)) {
            toast.error('الرجاء إدخال تاريخ وميناء المغادرة.');
            return;
        }

        setIsSaving(true);

        const filesToUpload = stagedFiles.map((sf, index) => ({
            file: sf.file,
            type: sf.type,
            id: `DOC-${Date.now()}-${index}`
        }));

        const newHistoryItem: StageHistoryItem = {
            stageId: request.currentStageId,
            stageName: currentStage.name,
            processor: user.name,
            timestamp: new Date().toISOString(),
            comment: comment,
            documents: [],
        };

        const updatedRequest: WorkflowRequest = {
            ...request,
            currentStageId: request.currentStageId + 1,
            lastModified: new Date().toISOString(),
            stageHistory: [...request.stageHistory, newHistoryItem],
        };

        if (request.currentStageId === 2) {
            updatedRequest.containerCount20ft = Number(container20ft);
            updatedRequest.containerCount40ft = Number(container40ft);
            updatedRequest.expectedDepartureDate = departureDate;
            updatedRequest.departurePort = departurePort;
        }

        await updateRequest(updatedRequest, filesToUpload);

        setComment('');
        setStagedFiles([]);
        setIsSaving(false);
        toast.success('تمت الموافقة على المرحلة والانتقال للمرحلة التالية.');
    };

    const handleUploadForType = (type: DocumentType) => {
        setSelectedDocTypeForUpload(type);
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && selectedDocTypeForUpload) {
            const newFiles = Array.from(e.target.files).map(file => ({ file, type: selectedDocTypeForUpload }));
            setStagedFiles(prev => [...prev, ...newFiles]);
        }
        e.target.value = '';
        setSelectedDocTypeForUpload(null);
    };
    const handleSaveBasicInfo = async () => {
        if (!user) return;
        try {
            setIsSaving(true);
            const now = new Date().toISOString();
            const updated: WorkflowRequest = {
                ...request,
                containerCount20ft: Number(container20ft) || 0,
                containerCount40ft: Number(container40ft) || 0,
                departurePort: departurePort || undefined,
                blDate: blDate || undefined,
                lastModified: now,
            };
            await updateRequest(updated);
            toast.success('تم حفظ المعلومات الأساسية بنجاح');
        } catch (e: any) {
            toast.error(`تعذر حفظ المعلومات: ${e?.message || 'خطأ غير معروف'}`);
        } finally {
            setIsSaving(false);
        }
    };

    const removeStagedFile = (index: number) => {
        setStagedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleHistoryItemUpdate = async (updatedItem: StageHistoryItem, newFiles: { file: File; type: string; id: string }[]) => {
        if (!user) return;
        const now = new Date().toISOString();

        updatedItem.modified = { processor: user.name, timestamp: now };

        const newStageHistory = request.stageHistory.map(h => h.stageId === updatedItem.stageId ? updatedItem : h);

        const updatedRequest: WorkflowRequest = {
            ...request,
            lastModified: now,
            stageHistory: newStageHistory,
        };

        await updateRequest(updatedRequest, newFiles);
        setEditingHistoryItem(null);
        toast.success('تم تعديل المرحلة بنجاح!');
    };

    const handleStageClick = (stageId: number) => {
        const historyItem = request.stageHistory.find(item => item.stageId === stageId);
        if (historyItem) {
            setEditingHistoryItem(historyItem);
        } else {
            toast.error('لا يمكن تعديل هذه المرحلة حالياً.');
        }
    };

    const handleSaveBasicDates = async () => {
        if (!user) return;
        try {
            setIsSaving(true);
            const now = new Date().toISOString();
            const withArrivalHistory = expectedArrivalDate ? upsertArrivalDateInHistory(request, expectedArrivalDate, user.name || user.username || 'موظف') : { ...request, lastModified: now } as WorkflowRequest;
            const updated: WorkflowRequest = {
                ...withArrivalHistory,
                manufacturingDate: manufacturingDate || undefined,
                expectedDepartureDate: departureDate || undefined,
                expectedArrivalDate: expectedArrivalDate || undefined,
                lastModified: now,
            };
            await updateRequest(updated);
            toast.success('تم حفظ التواريخ الأساسية بنجاح');
        } catch (e: any) {
            toast.error(`تعذر حفظ التواريخ: ${e?.message || 'خطأ غير معروف'}`);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 animate-content-fade-in pb-20">
            <input type="file" multiple ref={fileInputRef} className="hidden" onChange={handleFileChange} />

            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-blue-500/20 text-blue-400">
                            <Package size={28} />
                        </div>
                        {request.title}
                    </h1>
                    <p className="text-slate-400">{request.description}</p>
                </div>
                <button
                    onClick={() => navigate('/workflow')}
                    className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors flex items-center gap-2"
                >
                    <ArrowRight size={18} className="rotate-180" />
                    <span>رجوع للقائمة</span>
                </button>
            </div>

            {/* Stepper */}
            <div className="glass-panel p-6 rounded-2xl overflow-x-auto">
                <div className="flex items-center min-w-[800px]">
                    {WORKFLOW_STAGES.map((stage, index) => {
                        const isCompleted = request.currentStageId > stage.id;
                        const isCurrent = request.currentStageId === stage.id;
                        const isFuture = request.currentStageId < stage.id;

                        return (
                            <div key={stage.id} className="flex-1 flex flex-col items-center relative group">
                                {index !== 0 && (
                                    <div className={`absolute top-5 right-[50%] w-full h-1 ${isCompleted ? 'bg-blue-500' : 'bg-slate-700'} -z-10 transition-colors duration-500`}></div>
                                )}
                                <button
                                    onClick={() => handleStageClick(stage.id)}
                                    disabled={isFuture}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center border-4 transition-all duration-300 z-10 relative
                                    ${isCompleted ? 'bg-blue-500 border-blue-500 text-white' :
                                            isCurrent ? 'bg-slate-900 border-blue-500 text-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.5)]' :
                                                'bg-slate-800 border-slate-700 text-slate-500'}
                                `}
                                >
                                    {isCompleted ? <Check size={18} /> : <span>{stage.id}</span>}
                                    {isCurrent && <span className="absolute inset-0 rounded-full animate-ping bg-blue-500/30"></span>}
                                </button>
                                <span className={`mt-3 text-xs font-semibold px-2 py-1 rounded-lg transition-colors
                                ${isCurrent ? 'text-blue-400 bg-blue-500/10' : isCompleted ? 'text-slate-300' : 'text-slate-500'}
                            `}>
                                    {stage.name}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {request.currentStageId <= WORKFLOW_STAGES.length && (
                        <div className="glass-panel p-6 rounded-2xl relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                            <div className="flex justify-between items-start mb-6 border-b border-slate-700/50 pb-4">
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-1">الإجراءات المطلوبة: {currentStage.name}</h2>
                                    <div className="flex items-center gap-2 text-sm text-slate-400">
                                        <User size={14} />
                                        <span>المسؤول: {currentStage.responsible}</span>
                                    </div>
                                </div>
                                <div className="px-3 py-1 rounded-lg bg-blue-500/10 text-blue-400 text-xs font-bold border border-blue-500/20">
                                    المرحلة الحالية
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="text-sm font-medium mb-2 block text-slate-300">التعليقات</label>
                                    <textarea
                                        value={comment}
                                        onChange={e => setComment(e.target.value)}
                                        placeholder="أضف تعليقاً..."
                                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-4 text-white focus:outline-none focus:border-blue-500/50 min-h-[100px]"
                                    />
                                </div>

                                {currentStage.requiredDocuments && currentStage.requiredDocuments.length > 0 && (
                                    <div>
                                        <label className="text-sm font-medium mb-2 block text-slate-300">المستندات المطلوبة</label>
                                        <div className="flex flex-wrap gap-2">
                                            {currentStage.requiredDocuments.map(docType => (
                                                <button
                                                    key={docType}
                                                    onClick={() => handleUploadForType(docType)}
                                                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors border border-slate-700"
                                                >
                                                    <Upload size={16} />
                                                    <span>رفع {request.currentStageId === 3 && docType === 'Invoice' ? 'Original Invoice' : docType}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {stagedFiles.length > 0 && (
                                    <div className="space-y-2 p-4 bg-slate-900/30 rounded-xl border border-slate-700/30">
                                        <h4 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2">
                                            <Paperclip size={14} />
                                            الملفات المرفقة:
                                        </h4>
                                        {stagedFiles.map((f, index) => (
                                            <div key={index} className="flex items-center justify-between bg-slate-800/50 p-3 rounded-lg border border-slate-700/50">
                                                <div className="flex items-center gap-3 truncate">
                                                    <div className="p-2 rounded-lg bg-blue-500/10 text-blue-400">
                                                        <FileText size={16} />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-sm text-white truncate font-medium">{f.file.name}</span>
                                                        <span className="text-xs text-slate-400">{request.currentStageId === 3 && f.type === 'Invoice' ? 'Original Invoice' : f.type}</span>
                                                    </div>
                                                </div>
                                                <button onClick={() => removeStagedFile(index)} className="text-slate-500 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/10 transition-all">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {request.currentStageId === 2 && (
                                    <ApprovalStageForm
                                        container20ft={container20ft} setContainer20ft={setContainer20ft}
                                        container40ft={container40ft} setContainer40ft={setContainer40ft}
                                        departureDate={departureDate} setDepartureDate={setDepartureDate}
                                        departurePort={departurePort} setDeparturePort={setDeparturePort}
                                    />
                                )}
                            </div>

                            <div className="mt-8 pt-6 border-t border-slate-700/50">
                                <button
                                    onClick={handleApprove}
                                    disabled={isSaving || (currentStage.id !== 3 && !areAllRequiredDocsStaged)}
                                    className="w-full py-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-lg hover:shadow-lg hover:shadow-blue-500/25 transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSaving ? (
                                        <span className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    ) : (
                                        <>
                                            <CheckCircle size={24} />
                                            <span>إنجاز المرحلة والانتقال للتالية</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="glass-panel p-6 rounded-2xl">
                        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2 border-b border-slate-700/50 pb-4">
                            <Clock size={20} className="text-purple-400" />
                            سجل الإجراءات
                        </h3>
                        <div className="relative space-y-8 before:absolute before:inset-0 before:mr-5 before:-ml-5 before:h-full before:w-0.5 before:bg-slate-700/50 md:before:mx-auto md:before:mr-0 md:before:ml-0">
                            {request.stageHistory.map((item, index) => (
                                <div key={index} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-slate-900 bg-slate-800 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                                        <Check size={16} className="text-green-400" />
                                    </div>
                                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 hover:border-blue-500/30 transition-colors cursor-pointer" onClick={() => handleStageClick(item.stageId)}>
                                        <div className="flex items-center justify-between mb-1">
                                            <time className="font-mono text-xs text-slate-400">{new Date(item.timestamp).toLocaleDateString('en-GB')}</time>
                                            <span className="text-xs font-bold text-blue-400">{item.processor}</span>
                                        </div>
                                        <div className="text-white font-bold mb-2">{item.stageName}</div>
                                        {item.comment && (
                                            <p className="text-slate-300 text-sm bg-slate-900/30 p-2 rounded-lg mb-2">
                                                {item.comment.replace('[ARRIVAL_DATE]', 'تاريخ الوصول المتوقع: ')}
                                            </p>
                                        )}
                                        {item.documents && item.documents.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {item.documents.map((doc, i) => (
                                                    <a
                                                        key={i}
                                                        href={doc.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-700 text-slate-300 hover:text-white hover:bg-slate-600 transition-colors"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <FileText size={10} />
                                                        <span className="truncate max-w-[100px]">{doc.fileName}</span>
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-1 space-y-6">
                    <div className="glass-panel p-6 rounded-2xl">
                        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2 border-b border-slate-700/50 pb-4">
                            <AlertCircle size={20} className="text-amber-400" />
                            معلومات أساسية
                        </h3>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center p-3 bg-slate-900/30 rounded-xl">
                                <span className="text-slate-400 text-sm">المعرف</span>
                                <span className="font-mono text-white font-bold bg-slate-800 px-2 py-1 rounded">{request.id}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-slate-900/30 rounded-xl">
                                <span className="text-slate-400 text-sm">النوع</span>
                                <Badge variant={request.type === 'استيراد' ? 'default' : 'secondary'}>{request.type}</Badge>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-slate-900/30 rounded-xl">
                                <span className="text-slate-400 text-sm">الأولوية</span>
                                <Badge variant={request.priority === 'عالية' ? 'destructive' : request.priority === 'متوسطة' ? 'warning' : 'default'}>{request.priority}</Badge>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-slate-900/30 rounded-xl">
                                <span className="text-slate-400 text-sm">تاريخ الإنشاء</span>
                                <span className="text-white font-mono">{new Date(request.creationDate).toLocaleDateString('en-GB')}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-slate-900/30 rounded-xl">
                                <span className="text-slate-400 text-sm">آخر تحديث</span>
                                <span className="text-white text-sm">{timeAgo(request.lastModified)}</span>
                            </div>

                            <div className="pt-4 border-t border-slate-700/50 space-y-4">
                                <NumberInput label="عدد الحاويات (20 قدم)" value={container20ft} onChange={setContainer20ft} icon={<Truck size={16} />} />
                                <NumberInput label="عدد الحاويات (40 قدم)" value={container40ft} onChange={setContainer40ft} icon={<Truck size={16} />} />
                                <div>
                                    <label className="block text-sm font-medium mb-2 text-slate-300">ميناء المغادرة</label>
                                    <div className="relative">
                                        <Ship size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                        <input
                                            value={departurePort}
                                            onChange={e => setDeparturePort(e.target.value)}
                                            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pr-10 px-4 text-white focus:outline-none focus:border-blue-500/50"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-slate-300">رقم البوليصة (BL)</label>
                                        <div className="relative">
                                            <FileText size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                            <input
                                                value={blNumber}
                                                onChange={e => setBlNumber(e.target.value)}
                                                className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pr-10 px-4 text-white focus:outline-none focus:border-blue-500/50"
                                                placeholder="BL Number"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-slate-300">تاريخ البوليصة (BL)</label>
                                        <div className="relative">
                                            <CalendarDays size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                            <input
                                                type="date"
                                                value={blDate}
                                                onChange={e => setBlDate(e.target.value)}
                                                className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pr-10 px-4 text-white focus:outline-none focus:border-blue-500/50"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-slate-300">رقم الفاتورة (CI)</label>
                                        <div className="relative">
                                            <FileText size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                            <input
                                                value={ciNumber}
                                                onChange={e => setCiNumber(e.target.value)}
                                                className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pr-10 px-4 text-white focus:outline-none focus:border-blue-500/50"
                                                placeholder="CI Number"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-slate-300">نوع البضاعة</label>
                                        <div className="relative">
                                            <Package size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                            <input
                                                value={goodsType}
                                                onChange={e => setGoodsType(e.target.value)}
                                                className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pr-10 px-4 text-white focus:outline-none focus:border-blue-500/50"
                                                placeholder="Goods Type"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={handleSaveBasicInfo}
                                    disabled={isSaving}
                                    className="w-full py-2.5 rounded-xl bg-slate-800 text-white hover:bg-slate-700 border border-slate-700 transition-colors flex items-center justify-center gap-2"
                                >
                                    {isSaving ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : <Save size={16} />}
                                    <span>حفظ المعلومات</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="glass-panel p-6 rounded-2xl">
                        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2 border-b border-slate-700/50 pb-4">
                            <CalendarDays size={20} className="text-blue-400" />
                            تواريخ أساسية
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2 text-slate-300">تاريخ التصنيع</label>
                                <input
                                    type="date"
                                    value={manufacturingDate}
                                    onChange={e => setManufacturingDate(e.target.value)}
                                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 px-4 text-white focus:outline-none focus:border-blue-500/50"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2 text-slate-300">تاريخ المغادرة</label>
                                <input
                                    type="date"
                                    value={departureDate}
                                    onChange={e => setDepartureDate(e.target.value)}
                                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 px-4 text-white focus:outline-none focus:border-blue-500/50"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2 text-slate-300">تاريخ الوصول</label>
                                <input
                                    type="date"
                                    value={expectedArrivalDate}
                                    onChange={e => setExpectedArrivalDate(e.target.value)}
                                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 px-4 text-white focus:outline-none focus:border-blue-500/50"
                                />
                            </div>
                            <button
                                onClick={handleSaveBasicDates}
                                disabled={isSaving}
                                className="w-full py-2.5 rounded-xl bg-slate-800 text-white hover:bg-slate-700 border border-slate-700 transition-colors flex items-center justify-center gap-2 mt-2"
                            >
                                {isSaving ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : <Save size={16} />}
                                <span>حفظ التواريخ</span>
                            </button>
                        </div>
                    </div>

                    <DepartureCountdown request={request} />
                </div>
            </div>

            {editingHistoryItem && (
                <ModernStageEditModal
                    item={editingHistoryItem}
                    onClose={() => setEditingHistoryItem(null)}
                    onSave={handleHistoryItemUpdate}
                />
            )}
        </div>
    );
};

export default ModernWorkflowDetailScreen;
